import { api } from './api';
import { OCIScore, OCIConfig } from '../types/oci.types';

export const ociService = {
  /**
   * Get OCI score for a specific page
   */
  async getOCIScore(
    pageId: number,
    engine: 'google' | 'bing' = 'google',
    window: 7 | 28 | 90 = 28
  ): Promise<OCIScore> {
    const response = await api.get<any>(
      `/coreapi/pages/${pageId}/oci`,
      { params: { engine: engine.toUpperCase(), windowDays: window } }
    );
    console.log('🔍 [OCI SERVICE] Raw response:', response);
    console.log('🔍 [OCI SERVICE] Response status:', response.status);
    console.log('🔍 [OCI SERVICE] Response data:', response.data);
    
    // Handle 202 ACCEPTED - OCI computation in progress
    if (response.status === 202) {
      console.log('⏳ [OCI SERVICE] OCI computation in progress (202 ACCEPTED)');
      throw new Error('OCI_COMPUTING');
    }
    
    // Transform backend response to frontend format
    const data = response.data;
    
    // Backend uses 'factorsJson' field, frontend expects 'factors' array
    const factorsSource = data.factorsJson || data.factors;
    console.log('🔍 [OCI SERVICE] Factors source:', factorsSource, 'Type:', typeof factorsSource);
    
    if (factorsSource) {
      if (typeof factorsSource === 'object' && !Array.isArray(factorsSource)) {
        console.log('🔄 [OCI SERVICE] Converting factorsJson object to factors array');
        data.factors = Object.entries(factorsSource).map(([name, value]) => ({
          name,
          value: typeof value === 'number' ? value : 0,
          contribution: typeof value === 'number' ? Math.abs(value) : 0,
          description: name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
        }));
        console.log('✅ [OCI SERVICE] Transformed factors:', data.factors);
      } else if (Array.isArray(factorsSource)) {
        console.log('✅ [OCI SERVICE] Factors already an array');
        data.factors = factorsSource;
      }
    } else {
      console.warn('⚠️ [OCI SERVICE] No factors in response');
      data.factors = [];
    }
    
    // Map backend field names to frontend
    return {
      pageId: data.pageId,
      score: data.ociScore,
      level: data.ociLevel,
      computedAt: data.computedAt,
      engine: data.engine.toLowerCase() as 'google' | 'bing',
      window: data.windowDays,
      factors: data.factors,
      minImpressionsRequired: data.minImpressionsRequired,
      actualImpressions: data.actualImpressions,
    } as OCIScore;
  },

  /**
   * Get OCI scores for multiple pages
   */
  async getBatchOCIScores(
    pageIds: number[],
    engine: 'google' | 'bing' = 'google',
    window: 7 | 28 | 90 = 28
  ): Promise<Record<number, OCIScore>> {
    const response = await api.post<Record<number, OCIScore>>(
      `/coreapi/oci/batch`,
      { pageIds, engine: engine.toUpperCase(), windowDays: window }
    );
    return response.data;
  },

  /**
   * Get OCI configuration (thresholds, factors, etc.)
   */
  async getOCIConfig(): Promise<OCIConfig> {
    const response = await api.get<OCIConfig>(`/coreapi/oci/config`);
    return response.data;
  },

  /**
   * Update OCI configuration (admin only)
   */
  async updateOCIConfig(config: Partial<OCIConfig>): Promise<OCIConfig> {
    const response = await api.put<OCIConfig>(`/coreapi/oci/config`, config);
    return response.data;
  },

  /**
   * Recompute OCI for a page
   */
  async recomputeOCI(
    pageId: number,
    engine: 'google' | 'bing' = 'google',
    window: 7 | 28 | 90 = 28
  ): Promise<OCIScore> {
    const response = await api.post<OCIScore>(
      `/coreapi/pages/${pageId}/oci/recompute`,
      { engine: engine.toUpperCase(), windowDays: window }
    );
    return response.data;
  },

  /**
   * Get OCI history for a page
   */
  async getOCIHistory(
    pageId: number,
    engine: 'google' | 'bing' = 'google',
    window: 7 | 28 | 90 = 28
  ): Promise<OCIScore[]> {
    const response = await api.get<any[]>(
      `/coreapi/pages/${pageId}/oci/history`,
      { params: { engine: engine.toUpperCase(), windowDays: window } }
    );
    
    // Transform backend response array to frontend format
    return (response.data || []).map(item => ({
      pageId: item.pageId,
      score: item.ociScore,
      level: item.ociLevel,
      computedAt: item.computedAt,
      engine: item.engine.toLowerCase() as 'google' | 'bing',
      window: item.windowDays,
      factors: item.factorsJson || item.factors || [],
      minImpressionsRequired: item.minImpressionsRequired,
      actualImpressions: item.actualImpressions,
    }));
  },

  /**
   * Get OCI score for a page by URL
   */
  async getOCIScoreByUrl(
    pageUrl: string,
    engine: 'google' | 'bing' = 'google',
    window: 7 | 28 | 90 = 28
  ): Promise<OCIScore> {
    const response = await api.get<OCIScore>(
      `/coreapi/pages/oci/by-url`,
      { params: { pageUrl, engine: engine.toUpperCase(), windowDays: window } }
    );
    return response.data;
  },

  /**
   * Get OCI history for a page by URL
   */
  async getOCIHistoryByUrl(
    pageUrl: string,
    engine: 'google' | 'bing' = 'google',
    windowDays: 7 | 28 | 90 = 90
  ): Promise<OCIScore[]> {
    const response = await api.get<OCIScore[]>(
      `/coreapi/pages/oci/history/by-url`,
      { params: { pageUrl, engine: engine.toUpperCase(), windowDays } }
    );
    return response.data || [];
  },
};
